/**
 * Capabilities Hook
 * 
 * Manages the single-tier capabilities system for vendor services.
 * Capabilities define what types of work a vendor can perform.
 * 
 * **Key Features:**
 * - Fetch all active capabilities
 * - Sorted by display_order for consistent presentation
 * - Automatic cache management via React Query
 * 
 * **Use Cases:**
 * - Vendor profile capability selection
 * - RFQ capability requirements
 * - Vendor search and filtering
 * - Admin capability management
 * 
 * @returns {Object} Capabilities and loading state
 * @returns {Capability[]} capabilities - Array of capabilities
 * @returns {boolean} isLoading - Loading state
 * 
 * @example
 * const { capabilities, isLoading } = useCapabilities();
 * 
 * if (isLoading) return <Spinner />;
 * 
 * capabilities.map(cap => (
 *   <CapabilityCheckbox key={cap.id} label={cap.name} />
 * ));
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Capability {
  id: string;
  name: string;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export const useCapabilities = () => {
  const queryClient = useQueryClient();

  // Fetch all active capabilities
  const { data: capabilities = [], isLoading } = useQuery({
    queryKey: ['capabilities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('capabilities')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return data as Capability[];
    },
  });

  // Refetch capabilities
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['capabilities'] });
  };

  return {
    capabilities,
    isLoading,
    refetch,
  };
};
